/**
 * / — the Friday landing page.
 *
 * Served only to signed-out visitors: as soon as the session resolves, a
 * logged-in visitor is bounced to their workspace. The page is dressed as a
 * printed comic page in the Pulp Friday palette, with Borderlands-inked art.
 */

import { useEffect, useMemo, useRef } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'

import { useAuth } from '@/features/auth/authContext'
import { useLandingMotion } from '@/features/landing/useLandingMotion'
import { FridayMark } from '@/components/branding/FridayMark'

import heroCraig from '@/assets/landing/hero-craig.jpg'
import heroSmokey from '@/assets/landing/hero-smokey.jpg'
import streetWide from '@/assets/landing/street-wide.jpg'
import boomboxSticker from '@/assets/landing/boombox-sticker.png'

/** A hand-inked comic burst: a wobbly star with the label across it. */
function ComicBurst({ label }: { label: string }) {
  const points = useMemo(() => {
    const spikes = 13
    const outer = 97
    const inner = 66
    const out: string[] = []
    for (let i = 0; i < spikes * 2; i++) {
      const radius = i % 2 === 0 ? outer : inner
      const angle = (Math.PI * 2 * i) / (spikes * 2) - Math.PI / 2
      // A little wobble so the burst reads as drawn rather than plotted.
      const wobble = 1 + Math.sin(i * 2.7) * 0.05 + Math.cos(i * 1.3) * 0.03
      const x = 100 + Math.cos(angle) * radius * wobble
      const y = 100 + Math.sin(angle) * radius * wobble
      out.push(`${x.toFixed(1)},${y.toFixed(1)}`)
    }
    return out.join(' ')
  }, [])

  const [first, second] = label.split(' ')

  return (
    <div className="lp-burst" data-lp-burst>
      <svg
        className="lp-burst__art"
        viewBox="0 0 200 200"
        role="img"
        aria-label={label}
      >
        <polygon
          points={points}
          fill="var(--lp-rust)"
          stroke="var(--lp-ink)"
          strokeWidth="9"
          strokeLinejoin="round"
        />
        <g transform="rotate(-9 100 100)">
          <text x="100" y="93" textAnchor="middle" className="lp-burst__word">
            {first}
          </text>
          <text x="100" y="122" textAnchor="middle" className="lp-burst__word">
            {second}
          </text>
        </g>
      </svg>
    </div>
  )
}

const FEATURES = [
  {
    id: 'board',
    accent: 'lp-caption--teal',
    caption: 'The board',
    title: 'Drag it, drop it, done.',
    body: 'Move tasks between columns, edit them in place, break out the subtasks.',
    points: [
      'Labels, milestones and an activity feed',
      'Inline editing on the card itself',
      'List view when the board gets too wide',
    ],
  },
  {
    id: 'sprint',
    accent: 'lp-caption--sky',
    caption: 'The sprint',
    title: 'Plan it. Run it. Close it.',
    body: 'Plan a sprint, pull work into it, and take it from planned to active to done without leaving the board.',
    points: [
      'Add or remove tasks whenever you like',
      'Sprint scope lives right inside the board',
      'Every change lands in the feed',
    ],
  },
  {
    id: 'agent',
    accent: 'lp-caption--rust',
    caption: 'The agent',
    title: 'Let the agent push the card.',
    body: 'Point any MCP client at Friday. It can create tasks, drag them across columns and ship tickets.',
    points: [
      'Scoped API tokens per project',
      'Rate limits and an audit trail',
      'Works with the agent tools you already use',
    ],
  },
]

export function LandingPage() {
  const { user, loading } = useAuth()
  const navigate = useNavigate()
  const root = useRef<HTMLDivElement>(null)

  useLandingMotion(root)

  // A signed-in visitor has no business on the marketing page.
  useEffect(() => {
    if (loading || !user) return
    navigate({ to: '/w', replace: true })
  }, [loading, user, navigate])

  return (
    <div className="lp" ref={root}>
      <header className="lp-nav" data-lp-nav>
        <div className="lp-nav__brand">
          <FridayMark size={30} title="Friday" />
        </div>
        <nav className="lp-nav__links" aria-label="Sections">
          <a href="#board">The board</a>
          <a href="#sprint">The sprint</a>
          <a href="#agent">The agent</a>
        </nav>
        <div className="lp-nav__actions">
          <Link to="/signup" search={{ next: undefined }} className="lp-btn lp-btn--ghost">
            Create account
          </Link>
          <Link to="/login" search={{ next: undefined }} className="lp-btn lp-btn--primary">
            Log in
          </Link>
        </div>
      </header>

      <main>
        <section className="lp-hero">
          <div className="lp-panel lp-panel--hero lp-hero__panel" data-lp-hero-panel>
            <div className="lp-hero__copy">
              <p className="lp-caption lp-caption--rust" data-lp-copy>
                A lot can go down between Thursday and Saturday.
              </p>

              <h1 className="lp-hero__title" aria-label="Friday">
                {'FRIDAY'.split('').map((letter, index) => (
                  <span className="lp-hero__letter" data-lp-letter key={index} aria-hidden="true">
                    {letter}
                  </span>
                ))}
              </h1>

              <p className="lp-hero__lede" data-lp-copy>
                Project management for teams that ship.
              </p>

              <p className="lp-hero__sub" data-lp-copy>
                Boards, sprints and live updates for the whole team — plus an MCP server, so
                your AI agents can create the tasks and move the cards.
              </p>

              <div className="lp-hero__actions" data-lp-copy>
                <Link to="/login" search={{ next: undefined }} className="lp-btn lp-btn--primary lp-btn--lg">
                  Log in
                </Link>
                <Link to="/signup" search={{ next: undefined }} className="lp-btn lp-btn--ghost lp-btn--lg">
                  Create an account
                </Link>
              </div>

              <ul className="lp-facts" data-lp-copy>
                <li className="lp-fact">
                  <span className="lp-fact__term">Realtime</span>
                  <span className="lp-fact__desc">Every move lands on every screen.</span>
                </li>
                <li className="lp-fact">
                  <span className="lp-fact__term">Sprints</span>
                  <span className="lp-fact__desc">Plan it, run it, close it.</span>
                </li>
                <li className="lp-fact">
                  <span className="lp-fact__term">MCP server</span>
                  <span className="lp-fact__desc">Your agents work the board.</span>
                </li>
              </ul>
            </div>

            <div className="lp-hero__art">
              <figure className="lp-figure lp-figure--craig" data-lp-figure>
                <img
                  src={heroCraig}
                  alt="A man in a yellow hoodie sitting on his porch steps with a boombox."
                  width={1100}
                  height={1482}
                />
                <figcaption className="lp-caption lp-hero__note" data-lp-copy>
                  He said the board was clean. It was not.
                </figcaption>
              </figure>

              <figure className="lp-figure lp-figure--smokey" data-lp-figure>
                <img
                  src={heroSmokey}
                  alt="His neighbour grinning on the porch railing with a bag of chips."
                  width={1100}
                  height={1492}
                />
              </figure>

              <ComicBurst label="AGENTS WELCOME" />

              <img
                className="lp-sticker"
                data-lp-sticker
                src={boomboxSticker}
                alt=""
                width={700}
                height={646}
              />
            </div>
          </div>
        </section>

        <section className="lp-strip">
          <figure className="lp-strip__frame" data-lp-reveal>
            <img
              src={streetWide}
              alt="A sunny neighbourhood corner with a graffiti-covered shop and a parked car."
              width={1900}
              height={1044}
              loading="lazy"
            />
            <figcaption className="lp-caption lp-caption--rust lp-strip__caption">
              Friday. 4:55 PM. The board is open.
            </figcaption>
          </figure>
        </section>

        <section className="lp-features">
          {FEATURES.map((feature, index) => (
            <article
              key={feature.id}
              id={feature.id}
              className={`lp-feature${index === 1 ? ' lp-feature--offset' : ''}`}
              data-lp-reveal
            >
              <span className={`lp-caption ${feature.accent}`}>{feature.caption}</span>
              <h2 className="lp-feature__title">{feature.title}</h2>
              <p className="lp-feature__body">{feature.body}</p>
              <ul className="lp-feature__list">
                {feature.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </article>
          ))}
        </section>

        <section className="lp-cta">
          <div className="lp-cta__panel" data-lp-reveal>
            <div>
              <span className="lp-caption">Five o&rsquo;clock. The board is clean.</span>
              <h2 className="lp-cta__title">Get on the board.</h2>
              <p className="lp-cta__body">
                Log in, or make an account and start a workspace in under a minute.
              </p>
            </div>
            <div className="lp-cta__actions">
              <Link to="/login" search={{ next: undefined }} className="lp-btn lp-btn--lg">
                Log in
              </Link>
              <Link to="/signup" search={{ next: undefined }} className="lp-btn lp-btn--ghost lp-btn--lg">
                Create an account
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="lp-foot">
        <div className="lp-foot__inner">
          <span className="lp-nav__brand">
            <FridayMark size={22} compact title="Friday" />
          </span>
          <div className="lp-foot__links">
            <a href="https://friday.thriv.es" target="_blank" rel="noreferrer">
              friday.thriv.es
            </a>
            <Link to="/login" search={{ next: undefined }}>Log in</Link>
          </div>
          <span>&copy; {new Date().getFullYear()} thriv.es</span>
        </div>
      </footer>
    </div>
  )
}
